import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Bugsnag from '@bugsnag/js';
import { AppilyAuthWelcome, AppilyOverlay } from 'Components/appilyAuth';
import { IRootState } from '@store/root/rootState';
import PageLoader from 'Components/loader/pageLoader';
import APPILY_TYPE from '@src/constants/appilyTypeConstants';
import LOCATION_MODULE, {
  isModuleEnabledForPlatform,
} from '@src/constants/locationModuleConstants';

/**
 * Fetches the Sidekick anonymous user ID from the Sidekick session endpoint.
 * Returns undefined if the call fails or no anonymous user ID is available (graceful degradation).
 */
const fetchSidekickAnonymousUserId = async (): Promise<string | undefined> => {
  const sidekickUrl = process.env.REACT_APP_SIDEKICK_URL;
  if (!sidekickUrl) {
    return undefined;
  }

  try {
    const response = await axios.get(`${sidekickUrl}/auth/session`, {
      withCredentials: true,
    });
    // eslint-disable-next-line camelcase
    return response.data?.session?.anonymous_user_id;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to fetch Sidekick session:', error);
    return undefined;
  }
};

interface Props {
  formTitle: string;
  formSubTitle: string;
  logo: string;
  appilyMode: string;
}

const pointsToStops = (points: any[], stops: any[]) => {
  if (!points || !stops) return [];
  return points
    .filter((point: any) => point.stopid)
    .map((point: any) => {
      const matchedStop = stops?.find(
        (stop: any) => stop.stopid === point.stopid
      );
      return {
        ...matchedStop,
        point_id: point.id,
        media_id: point.media_id,
      };
    });
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const AppilyAuth = ({ formTitle, formSubTitle, logo, appilyMode }: Props) => {
  const [cxRedirectUrl, setCxRedirectUrl] = useState('');
  const [appilyOverlay, setAppilyOverlay] = useState(false);
  const [newTabRef, setNewTabRef] = useState<Window | null>(null);
  const [mainMediaId, setMainMediaId] = useState<any | null>(null);
  const [mainMediaType, setMainMediaType] = useState<any | null>(null);
  const [emblem, setEmblem] = useState<string>('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [mainMediaUrl, setMainMediaUrl] = useState<string>('');
  const [showIframe, setShowIframe] = useState(false);

  const {
    tourid,
    locId,
    instId,
    cappexId,
    flexRegId,
    platform,
    locationModules,
  } = useSelector((state: IRootState) => state.tour);
  const { module, source, userkey, sessionId } = useSelector(
    (state: IRootState) => state?.analytics?.analytics
  );

  // Check if Sidekick is enabled for this partner and platform
  const isSidekickEnabled = isModuleEnabledForPlatform(
    locationModules,
    LOCATION_MODULE.SIDEKICK_AI,
    platform
  );

  const checkForMessage = useCallback((event: any) => {
    if (event.data?.type === 'appily_tab_closed') {
      window.parent.postMessage({ message: ':::personalization' }, '*');
      window.parent.postMessage(
        { message: 'RegistrationForm_Completed', millisecondsUntilClose: 1000 },
        '*'
      );
    }
  }, []);

  useEffect(() => {
    if (!instId || !locId || !tourid) return;

    const fetchData = async () => {
      try {
        const [stopsRes, pointsRes, emblemsRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_BASE_URL}api/v2/institutions/${instId}/locations/${locId}/stops/tours?tour_status=live&size=999`,
            { withCredentials: true }
          ),
          axios.get(
            `${process.env.REACT_APP_BASE_URL}api/v2/institutions/${instId}/locations/${locId}/tours/${tourid}/points?size=500&sort=point::ASC`,
            { withCredentials: true }
          ),
          axios.get(
            `${process.env.REACT_APP_BASE_URL}api/v2/institutions/${instId}/locations/${locId}/emblems`,
            { withCredentials: true }
          ),
        ]);

        const tourData = stopsRes.data.resources;
        const emblemData = emblemsRes.data.resources;
        setEmblem(emblemData.data[0]?.url || '');
        const tourName = Object.keys(tourData)[0];
        const stops = tourData[tourName]?.stops || [];

        const points = pointsRes.data.resources.data;
        const orderedStopsList = pointsToStops(points, stops);

        // Set mainMediaId once both are loaded
        if (orderedStopsList.length > 0) {
          // eslint-disable-next-line camelcase
          setMainMediaId(orderedStopsList[0]?.main_media_id || null);
          // eslint-disable-next-line camelcase
          setMainMediaType(orderedStopsList[0]?.main_media_type || null);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading tour stops or points:', error);
      }
    };

    fetchData();
  }, [instId, locId, tourid]);

  useEffect(() => {
    window.addEventListener('message', checkForMessage);
    return () => {
      window.removeEventListener('message', checkForMessage);
    };
  }, [checkForMessage]);

  const handleContinueButtonClick = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();

    let preopened: Window | null | undefined;

    if (appilyMode === APPILY_TYPE.APPILY) {
      preopened = window.open('', '_blank');
      setNewTabRef(preopened);
    } else if (appilyMode === APPILY_TYPE.APPILY_IFRAME) {
      setShowIframe(true);
    }

    // Fetch Sidekick anonymous user ID if Sidekick is enabled for this partner
    let sidekickAnonymousUserId: string | undefined;
    if (isSidekickEnabled) {
      sidekickAnonymousUserId = await fetchSidekickAnonymousUserId();
    }

    // Build query params, only include sidekick_anonymous_user_id if available
    let url = `${process.env.REACT_APP_BASE_URL}api/v2/experience/${locId}/tour/${tourid}/appily/session?source=${source}&module_id=${module}&pl=${platform}&userkey=${userkey}&sessionid=${sessionId}`;
    if (sidekickAnonymousUserId) {
      url += `&sidekick_anonymous_user_id=${encodeURIComponent(
        sidekickAnonymousUserId
      )}`;
    }

    axios
      .post(url, {}, { withCredentials: true })
      .then(response => {
        const uuid = response?.data?.resources;
        const flexRegParams = flexRegId?.split('.');
        const redirectUrl = `${process.env.REACT_APP_CAPPEX_URL}/register/${flexRegParams[0]}/${flexRegParams[1]}?tourDataId=${uuid}&tourCollegeId=${cappexId}&tourLocation=${locId}`;
        setCxRedirectUrl(redirectUrl);
        if (appilyMode === APPILY_TYPE.APPILY) {
          setAppilyOverlay(true);
        }

        if (preopened && !preopened.closed) {
          preopened.location.href = redirectUrl;
          preopened.focus();
        } else {
          // Popup blocked
          Bugsnag.notify(
            new Error('Popup blocked - new window tab could not be opened')
          );
        }
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('POST request failed:', err);
        // Optionally close the preopened tab or show an error page inside it:
        if (preopened && !preopened.closed) {
          preopened.document.write(
            '<p>Something went wrong. You can close this tab.</p>'
          );
        }
      });
  };

  useEffect(() => {
    if (!mainMediaType) return;
    let mediaUrl = '';
    const img = new Image();
    const env = process.env.REACT_APP_ASSETS_URL;
    if (mainMediaType === 'panorama') {
      mediaUrl = `${env}/media/${locId}/panoramas/${mainMediaId}/4096.jpg`;
    } else if (mainMediaType === 'photo') {
      mediaUrl = `${env}/media/${locId}/photos/${mainMediaId}/1024.jpg`;
    }
    img.src = mediaUrl;

    img.onload = () => {
      setImageLoaded(true);
      setMainMediaUrl(mediaUrl);
    };
    img.onerror = () => {
      // eslint-disable-next-line no-console
      console.error('Failed to load image:', mediaUrl);
      setImageLoaded(false);
    };
  }, [mainMediaType, mainMediaId, locId]);

  const handleOverlayContinueClick = (
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    e.preventDefault();
    if (newTabRef && !newTabRef.closed) {
      newTabRef.focus();
    } else {
      const newTab = window.open(cxRedirectUrl, '_blank');
      setNewTabRef(newTab);
    }
  };

  if (!imageLoaded) return <PageLoader isVisible />;

  return (
    <>
      {appilyOverlay && (
        <AppilyOverlay
          cxLink={cxRedirectUrl}
          onCxLinkClick={handleOverlayContinueClick}
          onCloseOverlay={() => setAppilyOverlay(false)}
        />
      )}
      {mainMediaUrl && (
        <AppilyAuthWelcome
          mainMediaUrl={mainMediaUrl}
          mainMediaType={mainMediaType}
          formTitle={formTitle}
          formSubTitle={formSubTitle}
          logo={emblem}
          onContinueClick={handleContinueButtonClick}
          showIframe={showIframe}
          setShowIframe={setShowIframe}
          cxRedirectUrl={cxRedirectUrl}
          appilyMode={appilyMode}
        />
      )}
    </>
  );
};

export default AppilyAuth;
try {
    // @ts-ignore
    AppilyAuth.displayName = "AppilyAuth";
    // @ts-ignore
    AppilyAuth.__docgenInfo = { "description": "", "displayName": "AppilyAuth", "props": { "formTitle": { "defaultValue": null, "description": "", "name": "formTitle", "required": true, "type": { "name": "string" } }, "formSubTitle": { "defaultValue": null, "description": "", "name": "formSubTitle", "required": true, "type": { "name": "string" } }, "logo": { "defaultValue": null, "description": "", "name": "logo", "required": true, "type": { "name": "string" } }, "appilyMode": { "defaultValue": null, "description": "", "name": "appilyMode", "required": true, "type": { "name": "string" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/containers/appilyAuth/AppilyAuth.tsx#AppilyAuth"] = { docgenInfo: AppilyAuth.__docgenInfo, name: "AppilyAuth", path: "src/containers/appilyAuth/AppilyAuth.tsx#AppilyAuth" };
}
catch (__react_docgen_typescript_loader_error) { }